import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { ServiceCategory as Category, Service, Unit } from '@/shared/model';
import { CATEGORIES_DB, UNITS_DB, SERVICES_DB, CATALOG_VERSION } from '../entities/service/data/initialData';
import { mergeServicesData, ImportStats, IncomingServiceData, MergeResult } from '../entities/service/lib/importer';
import { parseSafeNumber } from '@/shared/lib/number';
import { MANUAL_FIELDS, migrateServiceStore, resolveLocalizationKeys } from './catalogMigrations';
import type { CatalogOverride, UserService } from '@/shared/validation/schemas';
import { useAuthStore } from '@/stores/useAuthStore';
import { enqueueCatalogChange } from '@/shared/lib/catalogQueue';
import type { CatalogChangeRecord } from '@/entities/sync/model/types';

function applyUpdater<T>(updater: T | ((prev: T) => T), prev: T): T {
  return typeof updater === 'function' ? (updater as (prev: T) => T)(prev) : updater;
}

// ---------------------------------------------------------------------------
// Write-back infrastructure (CAT-02 / CAT-03)
// Module-level to avoid per-keystroke PUT floods.
// Pitfall 7: triggers live INSIDE action bodies — NOT in set() subscribers.
// Pitfall 8: useAuthStore dep is uni-directional; useAuthStore does NOT import this store.
// ---------------------------------------------------------------------------

/** Per-serviceId debounce timers — keyed by id to avoid cross-service interference. */
const pendingSyncTimers = new Map<string, ReturnType<typeof setTimeout>>();

/**
 * Schedule a debounced sync for a service.
 * Clears any existing timer for the same serviceId before setting a new one.
 * syncFn is always the one from the LAST call — fires with the latest state (Pitfall 5 safe).
 */
function scheduleSync(serviceId: string, syncFn: () => void, delayMs = 500): void {
  const existing = pendingSyncTimers.get(serviceId);
  if (existing) clearTimeout(existing);
  const timer = setTimeout(() => {
    pendingSyncTimers.delete(serviceId);
    syncFn();
  }, delayMs);
  pendingSyncTimers.set(serviceId, timer);
}

/**
 * Fire-and-forget PUT /api/catalog/overrides or DELETE when reverted to default (CAT-02).
 * Sends only the delta fields that differ from SERVICES_DB bundle defaults (revert detection).
 * CR-01: price clamped via Math.max(0, parseSafeNumber).
 * WR-01: name sent only when non-empty and manually set (!nameKey).
 */
async function syncOverrideToServer(serviceId: string, service: Service): Promise<void> {
  const bundleDefault = SERVICES_DB.find((s) => s.id === serviceId);
  if (!bundleDefault) return; // Unknown id — skip

  const priceMatchesDefault = service.priceLabor === bundleDefault.priceLabor;
  // nameKey present → still using i18n name (not manual); name equal to bundle default → unchanged
  const nameMatchesDefault = !!service.nameKey || service.name === bundleDefault.name;

  if (priceMatchesDefault && nameMatchesDefault) {
    // Reverted to bundle defaults → DELETE the override row
    await fetch(`/api/catalog/overrides/${encodeURIComponent(serviceId)}`, {
      method: 'DELETE',
      signal: AbortSignal.timeout(10_000),
    }).catch(() => { /* fire-and-forget */ });
    return;
  }

  // One or more fields differ → PUT the override delta
  const body: Record<string, unknown> = { service_id: serviceId };
  if (!priceMatchesDefault) {
    body.price_labor = Math.max(0, parseSafeNumber(service.priceLabor)); // CR-01
  }
  // WR-01: send name only when it's a non-empty manual override (no nameKey = user renamed)
  if (!nameMatchesDefault && !service.nameKey && service.name.length > 0) {
    body.name = service.name;
    body.name_is_manual = true;
  }

  await fetch('/api/catalog/overrides', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    signal: AbortSignal.timeout(10_000),
    body: JSON.stringify(body),
  }).catch(() => { /* fire-and-forget */ });
}

/**
 * Fire-and-forget PUT /api/catalog/services/:id for a custom service (CAT-03).
 * CR-01: price clamped via Math.max(0, parseSafeNumber).
 */
async function syncCustomServiceToServer(service: Service): Promise<void> {
  await fetch(`/api/catalog/services/${encodeURIComponent(service.id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    signal: AbortSignal.timeout(10_000),
    body: JSON.stringify({
      name: service.name,
      unit: service.unit,
      category_id: service.categoryId ?? null,
      unit_type: service.unitType ?? null,
      price_labor: Math.max(0, parseSafeNumber(service.priceLabor)), // CR-01
    }),
  }).catch(() => { /* fire-and-forget */ });
}

interface ServiceState {
  rawServices: Service[];
  setServices: (servicesOrUpdater: Service[] | ((prev: Service[]) => Service[])) => void;
  rawCategories: Category[];
  setCategories: (categoriesOrUpdater: Category[] | ((prev: Category[]) => Category[])) => void;
  units: Unit[];
  setUnits: (unitsOrUpdater: Unit[] | ((prev: Unit[]) => Unit[])) => void;

  createService: (serviceData: Partial<Service>) => Service;
  updateService: (id: string, fieldOrUpdates: string | Partial<Service>, value?: any) => void;
  deleteService: (id: string) => boolean;
  createCategory: (name: string, type?: string) => void;
  updateCategory: (id: string, field: keyof Category, value: any) => void;
  deleteCategory: (id: string) => boolean;
  createUnit: (name: string) => void;
  updateUnit: (id: string, name: string) => void;
  deleteUnit: (id: string) => boolean;
  /**
   * SAFE-03: computes the merge from the store's RAW arrays (rawServices, rawCategories) and
   * calls no setter — a pure preview. Deliberately reads the raw arrays rather than the
   * localized ones (see useDatabase's `services`/`categories` memos): merging from translated
   * display names and writing the result back into raw storage would permanently bake those
   * names in and orphan the `nameKey` fields. Reading raw here makes that mistake unreachable
   * from the feature layer that will call this (plan 34-05's preview modal).
   */
  previewMergedServices: (incoming: IncomingServiceData[]) => MergeResult;
  /**
   * SAFE-03: commits an already-computed MergeResult (from previewMergedServices) without
   * recomputing it — appends any new categories, then replaces rawServices, then returns the
   * result's stats. Threading a precomputed result through (rather than recomputing at confirm
   * time) removes a time-of-check-to-time-of-use gap between what a preview shows and what gets
   * written.
   */
  applyMergedServices: (result: MergeResult) => ImportStats;
  resetServiceData: () => void;
  runMigration: () => void;
  /**
   * Idempotent merge of server catalog overrides into rawServices.
   * Called once after login; skips isCustom services and unknown service_ids.
   * Price applied via parseSafeNumber (CLAUDE.md rule 8).
   * nameKey cleared when name_is_manual name override is applied (mirrors updateService lines 77-79).
   */
  applyServerOverrides: (overrides: CatalogOverride[]) => void;
  /**
   * Merge-safe apply of server custom services (CAT-03, Pitfall 5).
   * Keeps local-only customs (offline-created, not yet on server) so race with
   * migrateCatalogToCloud does not drop in-flight services.
   * Never touches system (isCustom:false) services.
   * Price mapped via Math.max(0, parseSafeNumber) (CR-01).
   */
  applyCustomServices: (services: UserService[]) => void;
}

export const useServiceStore = create<ServiceState>()(
  persist(
    (set, get) => ({
      rawServices: SERVICES_DB as unknown as Service[],
      rawCategories: CATEGORIES_DB as unknown as Category[],
      units: UNITS_DB as Unit[],

      setServices: (servicesOrUpdater) => set((state) => ({
        rawServices: applyUpdater(servicesOrUpdater, state.rawServices),
      })),

      setCategories: (categoriesOrUpdater) => set((state) => ({
        rawCategories: applyUpdater(categoriesOrUpdater, state.rawCategories),
      })),

      setUnits: (unitsOrUpdater) => set((state) => ({
        units: applyUpdater(unitsOrUpdater, state.units),
      })),

      createService: (serviceData) => {
        // Phase 37 CATALOG-02 decision: no code change. Custom services are the user's by
        // definition (isCustom: true), are skipped entirely by mergeBaseCatalogIntoServices,
        // and never gain a manualFields entry — there is no bundle default to diverge from.
        // newService.manualFields stays undefined unless serviceData explicitly supplies it
        // (it never does from any call site in this codebase).
        const newService: Service = {
          id: serviceData.id || `custom_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          name: serviceData.name || 'Новая услуга',
          unit: serviceData.unit || 'm2',
          categoryId: serviceData.categoryId || 'other',
          ...serviceData,       // caller overrides applied first
          // WR-02 (37-REVIEW.md): isCustom asserted LAST — no caller-supplied `isCustom` in
          // `...serviceData` can ever override the custom-service invariant. mergeBaseCatalogIntoServices
          // and backfillManualFieldsFromDivergence both key their "skip custom services" branch on
          // `s.isCustom` being truthy; a falsy override here (even unreachable from any current
          // call site) would let a custom service get merged/backfilled against the base catalog.
          isCustom: true,
          // CR-01: applied LAST so ...serviceData spread cannot overwrite the clamp
          // WR-02: parseSafeNumber handles Infinity/NaN → 0; parseFloat does not
          priceLabor: Math.max(0, parseSafeNumber(serviceData.priceLabor ?? 0)),
        };
        set((state) => ({ rawServices: [newService, ...state.rawServices] }));

        // Write-back (CAT-03): one-shot immediate PUT for logged-in users (no debounce — single create action)
        const { userId } = useAuthStore.getState();
        if (userId) {
          void syncCustomServiceToServer(newService);
          // HARDEN-02: enqueue alongside the fire-and-forget write-back (safety net for offline).
          // Idempotent with server: PUT same data = no-op when online double-write occurs.
          const record: CatalogChangeRecord = {
            id: crypto.randomUUID(),
            serviceId: newService.id,
            operation: 'upsert_custom',
            payload: {
              name: newService.name,
              unit: newService.unit,
              category_id: newService.categoryId ?? null,
              unit_type: newService.unitType ?? null,
              price_labor: Math.max(0, parseSafeNumber(newService.priceLabor)),
            },
            timestamp: new Date().toISOString(),
            schemaVersion: 1,
          };
          void enqueueCatalogChange(record);
        }

        return newService;
      },

      updateService: (id, fieldOrUpdates, value?) => {
        set((state) => {
          let updates: Partial<Service> = {};
          if (typeof fieldOrUpdates === 'string') {
            updates = { [fieldOrUpdates]: value };
          } else {
            updates = fieldOrUpdates;
          }
          return {
            rawServices: state.rawServices.map((s) => {
              if (s.id !== id) return s;
              const finalUpdates = { ...updates };
              if (updates.priceLabor !== undefined) {
                // WR-02: parseSafeNumber guards against Infinity/NaN; parseFloat("Infinity") = Infinity
                finalUpdates.priceLabor = Math.max(0, parseSafeNumber(updates.priceLabor));
              }

              // Phase 37 CATALOG-02: record which fields the user has explicitly claimed as
              // theirs, SYNCHRONOUSLY in this reducer body — NOT in the debounced write-back
              // closure below, which is gated on login state (`if (!userId) return`) and must
              // never be the only place a guest's edit is recorded (Mutation Proof 3). Runs
              // for every edit regardless of login state, in the same tick as the edit.
              // Skipped for isCustom services (no bundle default to compare against, and
              // custom services are the user's by definition — never base-merged).
              if (!s.isCustom) {
                const bundleDefault = SERVICES_DB.find((db) => db.id === id);
                if (bundleDefault) {
                  const claims = new Set(s.manualFields ?? []);
                  for (const field of MANUAL_FIELDS) {
                    if (!(field in updates)) continue;
                    const newValue = (finalUpdates as Record<string, unknown>)[field];
                    const baseValue = (bundleDefault as unknown as Record<string, unknown>)[field];
                    if (newValue === baseValue) {
                      claims.delete(field);
                    } else {
                      claims.add(field);
                    }
                  }
                  finalUpdates.manualFields = MANUAL_FIELDS.filter((f) => claims.has(f));
                  // 37-REVIEW.iter2.md CR-01(new)/WR-01(new): derive nameKey/descriptionKey from
                  // the FINAL claim set via the single shared resolver, on every edit — not just
                  // when updates.name/updates.description is truthy (that guard let an edit to
                  // '' claim the field in manualFields above while never clearing its key), and
                  // not just forward (claiming); this also RESTORES the key from bundleDefault
                  // the moment a field's claim is released by reverting to the exact base value.
                  Object.assign(finalUpdates, resolveLocalizationKeys(bundleDefault, claims));
                }
              }

              return { ...s, ...finalUpdates };
            }),
          };
        });

        // Write-back (CAT-02/CAT-03): debounced 500ms for logged-in users.
        // Reads latest state at fire-time (get() inside closure) — avoids stale captures.
        // Pitfall 7: trigger is here in the action body, NOT in a set() subscriber.
        const { userId } = useAuthStore.getState();
        if (!userId) return; // Guest — localStorage only, no network writes

        scheduleSync(id, () => {
          const svc = get().rawServices.find((s) => s.id === id);
          if (!svc) return;
          if (svc.isCustom) {
            void syncCustomServiceToServer(svc); // CAT-03
            // HARDEN-02: enqueue alongside write-back (safety net for offline)
            void enqueueCatalogChange({
              id: crypto.randomUUID(),
              serviceId: svc.id,
              operation: 'upsert_custom',
              payload: {
                name: svc.name,
                unit: svc.unit,
                category_id: svc.categoryId ?? null,
                unit_type: svc.unitType ?? null,
                price_labor: Math.max(0, parseSafeNumber(svc.priceLabor)),
              },
              timestamp: new Date().toISOString(),
              schemaVersion: 1,
            });
          } else {
            void syncOverrideToServer(id, svc); // CAT-02 (may DELETE if reverted to default)
            // HARDEN-02: determine operation by checking if service matches bundle defaults
            // (mirrors syncOverrideToServer's DELETE-vs-PUT logic)
            const bundleDefault = SERVICES_DB.find((s) => s.id === id);
            if (bundleDefault) {
              const priceMatchesDefault = svc.priceLabor === bundleDefault.priceLabor;
              const nameMatchesDefault = !!svc.nameKey || svc.name === bundleDefault.name;
              if (priceMatchesDefault && nameMatchesDefault) {
                // Reverted to default → DELETE the override row
                void enqueueCatalogChange({
                  id: crypto.randomUUID(),
                  serviceId: svc.id,
                  operation: 'delete_override',
                  payload: null,
                  timestamp: new Date().toISOString(),
                  schemaVersion: 1,
                });
              } else {
                // One or more fields differ → PUT the override delta
                const body: Record<string, unknown> = { service_id: svc.id };
                if (!priceMatchesDefault) {
                  body.price_labor = Math.max(0, parseSafeNumber(svc.priceLabor));
                }
                if (!nameMatchesDefault && !svc.nameKey && svc.name.length > 0) {
                  body.name = svc.name;
                  body.name_is_manual = true;
                }
                void enqueueCatalogChange({
                  id: crypto.randomUUID(),
                  serviceId: svc.id,
                  operation: 'upsert_override',
                  payload: body,
                  timestamp: new Date().toISOString(),
                  schemaVersion: 1,
                });
              }
            }
          }
        });
      },

      deleteService: (id) => {
        // Phase 37 CATALOG-02 decision: no code change. deleteService removes the whole
        // service (system or custom) from rawServices — there is no manualFields residue to
        // clean up because the record it would live on no longer exists.
        // IN-04: cancel any pending debounce timer for this service so it does not fire
        // up to 500ms after deletion (scheduleSync timer started by a prior updateService call).
        // Without this, the timer fires, finds svc=undefined, and exits safely — but the Map
        // entry holds a reference and the latent call is a code smell.
        const pendingTimer = pendingSyncTimers.get(id);
        if (pendingTimer) {
          clearTimeout(pendingTimer);
          pendingSyncTimers.delete(id);
        }

        // Capture service BEFORE removal so we can check isCustom for write-back (CAT-03)
        const service = get().rawServices.find((s) => s.id === id);
        set((state) => ({
          rawServices: state.rawServices.filter((s) => s.id !== id),
        }));

        // Write-back: only custom services have a DELETE /api/catalog/services/:id route.
        // System services do not support deletion (they are bundle entries).
        const { userId } = useAuthStore.getState();
        if (userId && service?.isCustom) {
          void fetch(`/api/catalog/services/${encodeURIComponent(id)}`, {
            method: 'DELETE',
            signal: AbortSignal.timeout(10_000),
          }).catch(() => { /* fire-and-forget */ });
          // HARDEN-02: enqueue alongside fire-and-forget write-back (safety net for offline)
          void enqueueCatalogChange({
            id: crypto.randomUUID(),
            serviceId: id,
            operation: 'delete_custom',
            payload: null,
            timestamp: new Date().toISOString(),
            schemaVersion: 1,
          });
        }

        return true;
      },

      createCategory: (name, type = 'general') => {
        const newId = `cat_${Date.now()}`;
        const newCat = { id: newId, name, type } as Category;
        set((state) => ({ rawCategories: [newCat, ...state.rawCategories] }));
      },

      updateCategory: (id, field, value) => set((state) => ({
        rawCategories: state.rawCategories.map((c) => {
          if (c.id !== id) return c;
          const updates: any = { [field]: value };
          if (field === 'name' && c.nameKey) {
            updates.nameKey = undefined;
          }
          return { ...c, ...updates };
        }),
      })),

      deleteCategory: (id) => {
        const { rawCategories, rawServices } = get();
        const categoryToDelete = rawCategories.find((c) => c.id === id);
        if (!categoryToDelete) return false;
        const isUsed = rawServices.some((s) => s.categoryId === id);
        if (isUsed) return false;
        set((state) => ({
          rawCategories: state.rawCategories.filter((c) => c.id !== id),
        }));
        return true;
      },

      createUnit: (name) => {
        const newId = `u_${Date.now()}`;
        const newUnit = { id: newId, name, type: 'custom' as const, calcRule: 'manual' as const };
        set((state) => ({ units: [...state.units, newUnit] }));
      },

      updateUnit: (id, name) => set((state) => ({
        units: state.units.map((u) => u.id === id ? { ...u, name } : u),
      })),

      deleteUnit: (id) => {
        const { units, rawServices } = get();
        const unit = units.find((u) => u.id === id);
        if (unit?.type === 'system') return false;
        const isUsed = rawServices.some((s) => s.unitType === id);
        if (isUsed) return false;
        set((state) => ({ units: state.units.filter((u) => u.id !== id) }));
        return true;
      },

      previewMergedServices: (incoming) => {
        // SAFE-03: reads the RAW arrays, not the localized `services`/`categories` from
        // useDatabase — see the doc comment on the state interface for why. Calls no setter.
        const { rawServices, rawCategories } = get();
        return mergeServicesData(rawServices, incoming, rawCategories);
      },

      applyMergedServices: (result) => {
        // SAFE-03: commits a result computed elsewhere (previewMergedServices) — does not
        // call mergeServicesData itself, so preview and commit cannot compute different answers.
        if (result.newCategories.length > 0) {
          set((state) => ({
            rawCategories: [...state.rawCategories, ...result.newCategories],
          }));
        }
        set({ rawServices: result.services });
        return result.stats;
      },

      resetServiceData: () => set({
        rawServices: SERVICES_DB as unknown as Service[],
        rawCategories: CATEGORIES_DB as unknown as Category[],
        units: UNITS_DB as Unit[],
      }),

      runMigration: () => {
        set((state) => {
          if (state.rawServices.length === 0 && state.rawCategories.length === 0 && state.units.length === 0) {
            return {
              rawServices: SERVICES_DB as unknown as Service[],
              rawCategories: CATEGORIES_DB as unknown as Category[],
              units: UNITS_DB as Unit[],
            };
          }

          let hasUpdates = false;

          // 37-REVIEW.md CR-01 (iteration 3): this resync IS a writer of the nameKey/descriptionKey
          // pair, so it must derive both keys from the same resolveLocalizationKeys(base, manualFields)
          // single decision point — not decide them independently. The prior version's guard only
          // checked nameKey drift but its body unconditionally also wrote descriptionKey, with zero
          // reference to manualFields: a user who had claimed 'description' (correctly clearing
          // descriptionKey) got that key silently resurrected the next time ANY service's nameKey
          // string changed in SERVICES_DB, resurfacing the base-catalog description over the user's
          // claimed edit despite manualFields still recording the claim.
          const migratedServices = state.rawServices.map((s) => {
            const ref = SERVICES_DB.find((db) => db.id === s.id);
            if (!ref) return s;
            const manual = new Set(s.manualFields ?? []);
            const resolvedKeys = resolveLocalizationKeys(ref, manual);
            if (resolvedKeys.nameKey === s.nameKey && resolvedKeys.descriptionKey === s.descriptionKey) {
              return s;
            }
            hasUpdates = true;
            return { ...s, ...resolvedKeys };
          });

          const migratedCategories = state.rawCategories.map((c) => {
            const ref = CATEGORIES_DB.find((db) => db.id === c.id);
            if (ref && c.nameKey && ref.nameKey !== c.nameKey) {
              hasUpdates = true;
              return { ...c, nameKey: ref.nameKey };
            }
            return c;
          });

          if (!hasUpdates) return state;

          return {
            rawServices: migratedServices,
            rawCategories: migratedCategories,
          };
        });
      },

      applyServerOverrides: (overrides) => {
        // WR-04: server-authoritative model for non-custom services.
        // Spec: "Пустой ответ = чистые дефолты" (12-CONTEXT.md).
        // The early-return that was here violated the spec: on re-login after having had overrides,
        // the store's rawServices in localStorage retained stale prices. Empty [] now resets all
        // system services to SERVICES_DB bundle defaults (same as delta with 0 overrides).
        // isCustom services are NEVER touched here — Phase 13 handles them (CLAUDE.md rule 12).

        // O(1) lookup: service_id → override
        const overrideMap = new Map(overrides.map((o) => [o.service_id, o]));

        set((state) => ({
          rawServices: state.rawServices.map((s) => {
            // Custom services (isCustom: true) are NOT in service_catalog_overrides —
            // Phase 13 handles them via user_services. Never touch them here (T-12-02).
            if (s.isCustom) return s;

            const override = overrideMap.get(s.id);

            if (!override) {
              // No server override for this service (including empty-array case).
              // Reset to SERVICES_DB bundle default so stale local prices are cleared.
              // If the service is no longer in SERVICES_DB (should not happen) → return unchanged.
              const bundleDefault = SERVICES_DB.find((db) => db.id === s.id);
              if (!bundleDefault) return s;
              // Phase 37 CATALOG-02 decision: clear the matching manualFields claims here too.
              // Without this a stale local claim would protect a field the server has already
              // confirmed is back to default, and the CATALOG-04 marker would lie (T-37-04).
              // categoryId/unitType are never touched by server overrides so they are left as-is.
              const clearedClaims = new Set(s.manualFields ?? []);
              clearedClaims.delete('priceLabor');
              clearedClaims.delete('name');
              clearedClaims.delete('description');
              return {
                ...s,
                priceLabor: bundleDefault.priceLabor,
                name: bundleDefault.name,
                description: bundleDefault.description,
                manualFields: MANUAL_FIELDS.filter((f) => clearedClaims.has(f)),
                // 37-REVIEW.iter2.md invariant: 'description' is unclaimed above (the server has
                // no description override concept at all, so its absence here always means
                // "not overridden") — restore descriptionKey from bundleDefault via the same
                // shared resolver updateService/mergeBaseCatalogIntoServices use, rather than
                // leaving descriptionKey frozen at whatever it was (the same bug class as
                // WR-01(new), just reached from this writer instead).
                ...resolveLocalizationKeys(bundleDefault, clearedClaims),
              };
            }

            // WR-01 (37-REVIEW.md, iteration 3): bundleDefault looked up here too, mirroring the
            // no-override branch above — needed both to restore a released name claim below and to
            // route nameKey/descriptionKey through the single resolveLocalizationKeys decision point
            // instead of a fourth independent place deciding the pair (the same architecture drift
            // instances #1-#5 all trace back to).
            const bundleDefault = SERVICES_DB.find((db) => db.id === s.id);
            if (!bundleDefault) return s; // Unknown id — skip (mirrors no-override branch's guard)

            const updates: Partial<Service> = {};

            // Price override: parseSafeNumber guards against NaN/malformed values; Math.max(0, …)
            // enforces the non-negative financial invariant (CLAUDE.md rule 8, T-12-01, CR-01).
            // Mirrors the clamp in updateService:90 and createService:67.
            if (override.price_labor !== null) {
              updates.priceLabor = Math.max(0, parseSafeNumber(override.price_labor));
            }

            const claims = new Set(s.manualFields ?? []);
            if (updates.priceLabor !== undefined) claims.add('priceLabor');

            // Name override: only when the user intentionally named the service (name_is_manual: true)
            // AND the name is a non-empty string (WR-01: "" would blank the display label and lose nameKey).
            // The schema-level .min(1) blocks "" at the fetch boundary; this guard is a defence-in-depth.
            // Mirrors updateService lines 77-79 in this file.
            // WR-01 (iteration 3): symmetric with release — if the override row no longer carries a
            // manual name (name_is_manual false, or name null/empty) but this service's local claim
            // set still has 'name' from an earlier sync, release the claim and restore the bundle
            // default name, rather than leaving a stale claim + a stale-cleared nameKey that nothing
            // ever restores while the row still exists (the gap the reviewer flagged).
            if (override.name !== null && override.name.length > 0 && override.name_is_manual) {
              updates.name = override.name;
              claims.add('name');
            } else if (claims.has('name')) {
              claims.delete('name');
              updates.name = bundleDefault.name;
            }

            // No applicable fields → return service unchanged (e.g. price_labor:null + name:null +
            // no stale claim to release)
            if (Object.keys(updates).length === 0) return s;

            // Phase 37 CATALOG-02 decision: keep the local claim in agreement with the server
            // override row — the same DELETE-vs-PUT "reverted to default" semantics
            // syncOverrideToServer already implements client -> server, mirrored here
            // server -> client so a re-login never leaves manualFields disagreeing with the
            // row that produced these updates.
            updates.manualFields = MANUAL_FIELDS.filter((f) => claims.has(f));
            // WR-01 (iteration 3): nameKey/descriptionKey derived from the shared resolver rather
            // than by direct assignment — makes this branch a consumer of the single decision point
            // instead of deciding the pair independently.
            Object.assign(updates, resolveLocalizationKeys(bundleDefault, claims));

            return { ...s, ...updates };
          }),
        }));
      },

      applyCustomServices: (services) => {
        // Pitfall 5 MERGE variant: keep local customs not yet on server (offline-created / pre-migration race).
        // Algorithm: serverIds set → keep rawServices where isCustom && !serverIds.has(id) → merge.
        // System services (isCustom:false) are NEVER touched here.
        // Pitfall 7: set() direct — no updateService() call → no write-back re-entrancy.
        //
        // Phase 37 CATALOG-02 decision: no code change to the manualFields mapping. Server
        // UserService rows carry no manual-claim metadata (custom services are outside the
        // base layer entirely — mergeBaseCatalogIntoServices returns isCustom services by
        // identity), so serverCustoms below must not fabricate a manualFields value. System
        // services are only filtered here (systemServices = ...filter(...)), never mapped, so
        // their existing manualFields claims pass through this function untouched.
        set((state) => {
          const serverIds = new Set(services.map((s) => s.id));
          // Local customs not yet uploaded (in-flight migration, offline-created)
          const localOnlyCustoms = state.rawServices.filter(
            (s) => s.isCustom && !serverIds.has(s.id),
          );
          // System services must be preserved as-is
          const systemServices = state.rawServices.filter((s) => !s.isCustom);
          // Map server UserService → local Service shape (CR-01: price clamped)
          const serverCustoms: Service[] = services.map((s) => ({
            id: s.id,
            name: s.name,
            unit: s.unit,
            categoryId: s.category_id ?? 'other',
            unitType: s.unit_type ?? undefined,
            priceLabor: Math.max(0, parseSafeNumber(s.price_labor)), // CR-01
            isCustom: true as const,
          }));
          return { rawServices: [...serverCustoms, ...localOnlyCustoms, ...systemServices] };
        });
      },
    }),
    {
      name: 'estimatix-services',
      version: CATALOG_VERSION,
      storage: createJSONStorage(() => localStorage),
      migrate: migrateServiceStore,
    },
  ),
);

// ---------------------------------------------------------------------------
// resetCatalogToDefaults (CAT-04)
// Exported standalone async fn — not a store action — to avoid Zustand set() circular
// complexity and keep the API consistent with how consumers (UI, tests) call reset.
// ---------------------------------------------------------------------------

/**
 * Reset all system service overrides to bundle defaults (CAT-04).
 * For logged-in users: DELETE /api/catalog/overrides (server) → applyServerOverrides([]) (local).
 * For guests: applyServerOverrides([]) only (resets local to SERVICES_DB bundle).
 * Custom services are NEVER deleted — user_services table is untouched.
 * Phase 12 WR-04: applyServerOverrides([]) skips isCustom services → customs survive.
 *
 * Phase 37 CATALOG-02 decision: no code change here. This function delegates entirely to
 * applyServerOverrides([]), whose "no override" reset branch already clears every system
 * service's priceLabor/name/description manualFields claims (see that function's comment) —
 * so resetCatalogToDefaults() clears the manualFields set entirely for every system service,
 * derived from that one branch rather than reimplemented here.
 */
export async function resetCatalogToDefaults(): Promise<void> {
  const { userId } = useAuthStore.getState();
  if (userId) {
    try {
      const res = await fetch('/api/catalog/overrides', {
        method: 'DELETE',
        signal: AbortSignal.timeout(10_000),
      });
      if (!res.ok) {
        // WR-04: server failed (e.g. 500) — skip local reset to avoid client/server desync.
        // fetch() only throws on network error; a 500 response does NOT enter catch.
        // Without this guard, a server error would leave server overrides intact while local
        // resets to defaults — re-login then re-applies server overrides, undoing the reset.
        return;
      }
    } catch {
      // Network error / timeout — same safe-default: skip local reset to keep client in sync.
      return;
    }
  }
  // applyServerOverrides([]) resets system services to SERVICES_DB bundle defaults.
  // isCustom services are skipped (Phase 12 implementation — confirmed safe).
  useServiceStore.getState().applyServerOverrides([]);
}